import React, {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  OpportunityNavSection,
  OpportunitySectionController,
} from './opportunity-section-navigation.types'

type OpportunitySectionNavigationContextValue = {
  sections: OpportunityNavSection[]
  activeSectionId: string
  isVisible: boolean
  setNavVisibilityAnchor: (node: HTMLElement | null) => void
  registerSectionController: (sectionId: string, controller: OpportunitySectionController) => void
  unregisterSectionController: (sectionId: string) => void
  navigateToSection: (sectionId: string) => void
}

const OpportunitySectionNavigationContext =
  createContext<OpportunitySectionNavigationContextValue | null>(null)

const SCROLL_SPY_THROTTLE_MS = 120

const measureScrollOffset = () => {
  const stickyCta = document.querySelector('.opportunity-hero-sticky-actions--visible')
  const sectionNav = document.querySelector('.opportunity-section-navigation-bar--visible')
  const stickyCtaHeight = stickyCta?.getBoundingClientRect().height ?? 0
  const isMobileViewport = window.matchMedia('(max-width: 768px)').matches
  const sectionNavHeight =
    !isMobileViewport && sectionNav ? sectionNav.getBoundingClientRect().height : 0

  return stickyCtaHeight + sectionNavHeight + 16
}

const resolveActiveSectionId = (sectionElements: HTMLElement[], offset: number) => {
  let nextActiveId: string | null = null
  let closestDistance = Number.POSITIVE_INFINITY

  sectionElements.forEach((sectionElement) => {
    const top = sectionElement.getBoundingClientRect().top - offset
    const distance = Math.abs(top)
    if (top <= 48 && distance < closestDistance) {
      closestDistance = distance
      nextActiveId = sectionElement.id
    }
  })

  if (!nextActiveId) {
    sectionElements.forEach((sectionElement) => {
      const distance = Math.abs(sectionElement.getBoundingClientRect().top - offset)
      if (distance < closestDistance) {
        closestDistance = distance
        nextActiveId = sectionElement.id
      }
    })
  }

  return nextActiveId
}

const getScrollOffset = () => measureScrollOffset()

const OpportunitySectionNavigationProvider = ({
  sections,
  children,
}: {
  sections: OpportunityNavSection[]
  children: React.ReactNode
}) => {
  const controllersRef = useRef<Map<string, OpportunitySectionController>>(new Map())
  const [activeSectionId, setActiveSectionId] = useState(sections[0]?.id ?? '')
  const [isVisible, setIsVisible] = useState(false)
  const [visibilityAnchor, setVisibilityAnchor] = useState<HTMLElement | null>(null)
  const visibilityAnchorRef = useRef<HTMLElement | null>(null)
  const isManualScrollRef = useRef(false)
  const activeSectionRef = useRef(activeSectionId)
  const sectionsRef = useRef(sections)

  activeSectionRef.current = activeSectionId
  sectionsRef.current = sections

  useEffect(() => {
    if (!sections.some((section) => section.id === activeSectionId)) {
      setActiveSectionId(sections[0]?.id ?? '')
    }
  }, [sections, activeSectionId])

  const setNavVisibilityAnchor = useCallback((node: HTMLElement | null) => {
    visibilityAnchorRef.current = node
    setVisibilityAnchor(node)
  }, [])

  const registerSectionController = useCallback(
    (sectionId: string, controller: OpportunitySectionController) => {
      controllersRef.current.set(sectionId, controller)
    },
    [],
  )

  const unregisterSectionController = useCallback((sectionId: string) => {
    controllersRef.current.delete(sectionId)
  }, [])

  const scrollToSection = useCallback((sectionId: string) => {
    const sectionElement = document.getElementById(sectionId)
    if (!sectionElement) {
      return
    }

    const targetPosition =
      sectionElement.getBoundingClientRect().top + window.scrollY - getScrollOffset()

    isManualScrollRef.current = true
    window.scrollTo({
      top: Math.max(targetPosition, 0),
      behavior: 'smooth',
    })

    window.setTimeout(() => {
      isManualScrollRef.current = false
    }, 1200)
  }, [])

  const navigateToSection = useCallback(
    (sectionId: string) => {
      activeSectionRef.current = sectionId
      setActiveSectionId(sectionId)
      isManualScrollRef.current = true

      const controller = controllersRef.current.get(sectionId)
      if (controller && !controller.isOpen()) {
        controller.ensureOpen()
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            scrollToSection(sectionId)
          })
        })
        return
      }

      scrollToSection(sectionId)
    },
    [scrollToSection],
  )

  useEffect(() => {
    if (!visibilityAnchor) {
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(!entry.isIntersecting)
      },
      { threshold: 0, rootMargin: '0px 0px -8px 0px' },
    )

    observer.observe(visibilityAnchor)
    return () => observer.disconnect()
  }, [visibilityAnchor])

  useEffect(() => {
    const bodyClass = 'opportunity-detail-section-nav-active'
    if (isVisible) {
      document.body.classList.add(bodyClass)
    } else {
      document.body.classList.remove(bodyClass)
    }

    return () => document.body.classList.remove(bodyClass)
  }, [isVisible])

  useEffect(() => {
    const sectionElements = sectionsRef.current
      .map(({ id }) => document.getElementById(id))
      .filter((element): element is HTMLElement => Boolean(element))

    if (sectionElements.length === 0) {
      return undefined
    }

    let scrollOffset = measureScrollOffset()
    let lastSpyRunAt = 0

    const refreshScrollOffset = () => {
      scrollOffset = measureScrollOffset()
    }

    const updateActiveSection = () => {
      if (isManualScrollRef.current) {
        return
      }

      const nextActiveId = resolveActiveSectionId(sectionElements, scrollOffset)
      if (nextActiveId && nextActiveId !== activeSectionRef.current) {
        activeSectionRef.current = nextActiveId
        startTransition(() => {
          setActiveSectionId(nextActiveId)
        })
      }
    }

    const handleScroll = () => {
      const now = performance.now()
      if (now - lastSpyRunAt < SCROLL_SPY_THROTTLE_MS) {
        return
      }

      lastSpyRunAt = now
      updateActiveSection()
    }

    refreshScrollOffset()
    updateActiveSection()

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', refreshScrollOffset, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', refreshScrollOffset)
    }
  }, [sections, isVisible])

  const value = useMemo(
    () => ({
      sections,
      activeSectionId,
      isVisible,
      setNavVisibilityAnchor,
      registerSectionController,
      unregisterSectionController,
      navigateToSection,
    }),
    [
      sections,
      activeSectionId,
      isVisible,
      setNavVisibilityAnchor,
      registerSectionController,
      unregisterSectionController,
      navigateToSection,
    ],
  )

  return (
    <OpportunitySectionNavigationContext.Provider value={value}>
      {children}
    </OpportunitySectionNavigationContext.Provider>
  )
}

const useOpportunitySectionNavigation = () => {
  const context = useContext(OpportunitySectionNavigationContext)
  if (!context) {
    throw new Error(
      'useOpportunitySectionNavigation must be used within OpportunitySectionNavigationProvider',
    )
  }
  return context
}

const useOptionalOpportunitySectionNavigation = () =>
  useContext(OpportunitySectionNavigationContext)

const useOpportunitySectionRegistration = (
  sectionId: string,
  isOpen: boolean,
  ensureOpen: () => void,
  enabled = true,
) => {
  const context = useOptionalOpportunitySectionNavigation()
  const isOpenRef = useRef(isOpen)
  const ensureOpenRef = useRef(ensureOpen)

  isOpenRef.current = isOpen
  ensureOpenRef.current = ensureOpen

  useEffect(() => {
    if (!enabled || !context) {
      return undefined
    }

    const { registerSectionController, unregisterSectionController } = context
    registerSectionController(sectionId, {
      ensureOpen: () => ensureOpenRef.current(),
      isOpen: () => isOpenRef.current,
    })

    return () => unregisterSectionController(sectionId)
  }, [context, enabled, sectionId])
}

export {
  OpportunitySectionNavigationProvider,
  useOpportunitySectionNavigation,
  useOpportunitySectionRegistration,
}
